import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, X } from 'lucide-react';

/**
 * UpdateBanner
 *
 * Listens for the 'planb-sw-update' event (dispatched from index.js when
 * serviceWorkerRegistration detects a new version waiting to activate).
 * Shows a small "New version available" bar; clicking Refresh tells the
 * waiting service worker to skip waiting and take control, then reloads
 * the page once it does (Step 14).
 *
 * Mount this once near the root of the app (e.g. in App.js), not per-page.
 */
export default function UpdateBanner() {
  const [waitingRegistration, setWaitingRegistration] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const handleUpdateAvailable = (event) => {
      setWaitingRegistration(event.detail);
    };

    window.addEventListener('planb-sw-update', handleUpdateAvailable);
    return () =>
      window.removeEventListener('planb-sw-update', handleUpdateAvailable);
  }, []);

  useEffect(() => {
    // Once the new service worker actually takes control (after skipWaiting),
    // reload so the user gets the new JS/CSS bundle instead of the cached old one.
    const handleControllerChange = () => {
      if (refreshing) return;
      setRefreshing(true);
      window.location.reload();
    };

    navigator.serviceWorker?.addEventListener(
      'controllerchange',
      handleControllerChange
    );
    return () =>
      navigator.serviceWorker?.removeEventListener(
        'controllerchange',
        handleControllerChange
      );
  }, [refreshing]);

  const handleRefresh = useCallback(() => {
    if (!waitingRegistration?.waiting) {
      // Fallback: no waiting worker reference (rare), just hard-reload.
      window.location.reload();
      return;
    }
    waitingRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
  }, [waitingRegistration]);

  if (!waitingRegistration) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[200] w-[92%] max-w-md">
      <div className="bg-[#0D1B3D] text-white rounded-xl shadow-lg px-4 py-3 flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0">
          <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight">
            New version available
          </p>
          <p className="text-xs text-white/60 leading-tight mt-0.5">
            Refresh to get the latest updates.
          </p>
        </div>

        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="bg-[#F59E0B] text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:opacity-90 transition disabled:opacity-50 whitespace-nowrap"
        >
          {refreshing ? 'Updating...' : 'Refresh'}
        </button>

        <button
          onClick={() => setWaitingRegistration(null)}
          aria-label="Dismiss"
          className="text-white/40 hover:text-white/70 transition p-1"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}